import { forwardRef, useImperativeHandle } from "react";
import { v4 as uuidv4 } from "uuid";

import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FaImage, FaSearchPlus, FaTimes } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { createPortal } from "react-dom";
import uploadFileToCloudinary from "../../../../utils/uploadFileToCloudinary";
import { showError } from "../../../../utils/toastUtils";

const UploadProgressButton = ({
  handleContinue,
  handleCancelUpload,
  isUploading,
  uploadProgress,
  uploadComplete,
}) => {
  return (
    <div className="mt-5">
      {uploadComplete ? (
        <button
          disabled
          className="w-full bg-status-success text-white py-3 rounded-lg font-medium text-sm opacity-60 cursor-not-allowed"
        >
          Images Uploaded ✓
        </button>
      ) : (
        <div className="flex gap-3 items-center">
          {/* Progress + Continue Button */}
          <div className="relative flex-1">
            {/* Background progress bar (fills gradually) */}
            {isUploading && (
              <div
                className="absolute left-0 top-0 h-full bg-primary/80 rounded-lg transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            )}

            <button
              onClick={handleContinue}
              disabled={isUploading}
              className="relative w-full bg-primary text-white py-3 rounded-lg font-medium text-sm
              hover:bg-primary/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed
              flex items-center justify-center gap-2 overflow-hidden shadow-sm"
            >
              {isUploading ? (
                <>
                  <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                  Uploading... {Math.floor(uploadProgress)}%
                </>
              ) : (
                "Continue"
              )}
            </button>
          </div>

          {/* Cancel Button — now separate and properly aligned */}
          {isUploading && (
            <button
              onClick={handleCancelUpload}
              className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-sm
              transition-colors shadow-sm"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const normalizePrefill = (arr = []) =>
  arr.map((img) => ({
    id: img.id || img._id || uuidv4(),
    uploadedUrl: img.uploadedUrl || img.url || null,
    public_id: img.public_id || img.publicId || null,
    category: img.category || "other",
    isUploading: false,
    uploadProgress: 100,
    blobUrl: null,
  }));

const ImageUploadSection = forwardRef(
  (
    {
      icon: Icon = FaImage,
      buttonText = "↓ Add at least 5 photos",
      description = " Max size 5MB | Formats: png, jpg, webp",
      badge = "5",
      onChange,
      prefillData,
      uploadCompleted,
    },
    ref
  ) => {
    // console.log("PrefillData:", prefillData);

    const fileInputRef = useRef(null);
    const addMoreBtnRef = useRef(null);
    const [previewImages, setPreviewImages] = useState(() =>
      normalizePrefill(prefillData)
    );
    const [thumbnailId, setThumbnailId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [fullScreenImage, setFullScreenImage] = useState(null);
    const [uploadControllers, setUploadControllers] = useState([]);
    const [totalProgress, setTotalProgress] = useState(0);
    const previewRef = useRef(previewImages);

    const categoryOptions = [
      { label: "Entrance", value: "entrance" },
      { label: "Exterior", value: "exterior" },
      { label: "Interior", value: "interior" },
      { label: "Bedroom", value: "bedroom" },
      { label: "Bathroom", value: "bathroom" },
      { label: "Kitchen", value: "kitchen" },
      { label: "Living Room", value: "living" },
      { label: "Amenities", value: "amenities" },
      { label: "Floor Plan", value: "floorplan" },
      { label: "Other", value: "other" },
    ];

    useEffect(() => {
      if (!prefillData || prefillData.length === 0) return;

      const normalized = normalizePrefill(prefillData);

      // If user already added local files (blobUrl exists), do NOT override.
      const hasLocalFiles = previewRef.current.some((img) => img.blobUrl);
      if (hasLocalFiles) return;

      // Compare URLs to avoid unnecessary re-renders.
      const currentUrls = previewRef.current
        .map((img) => img.uploadedUrl)
        .sort();

      const incomingUrls = normalized.map((img) => img.uploadedUrl).sort();

      const isSame =
        JSON.stringify(currentUrls) === JSON.stringify(incomingUrls);
      if (isSame) return;

      // Safe to apply normalized prefill
      setPreviewImages(normalized);

      // ensure thumbnail is assigned if not already
      if (!thumbnailId && normalized.length > 0) {
        setThumbnailId(normalized[0].id);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillData]);


    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === "Escape" && fullScreenImage) {
          setFullScreenImage(null);
        }
      };

      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }, [fullScreenImage]);

    // re-check uploads whenever previewImages changes
    useEffect(() => {
      if (previewImages.length > 0) {
        const allUploaded = previewImages.every((img) => img.uploadedUrl);
        setUploadComplete(allUploaded);
      } else {
        setUploadComplete(false);
      }
    }, [previewImages]);

    // Keep ref synced with latest state
    useEffect(() => {
      previewRef.current = previewImages;
    }, [previewImages]);

    //notify parent
    useEffect(() => {
      if (uploadComplete && onChange) {
        const uploadedImages = previewRef.current
          .filter((img) => img.uploadedUrl)
          .map((img) => ({
            url: img.uploadedUrl,
            public_id: img.public_id,
            category: img.category,
            isThumbnail: img.id === thumbnailId,
          }));
        onChange(uploadedImages);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadComplete]);

    // Recalculate total progress whenever previewImages change
    useEffect(() => {
      if (previewImages.length === 0) {
        setTotalProgress(0);
        return;
      }

      const uploadingImages = previewImages.filter(
        (img) => img.isUploading || !img.uploadedUrl
      );

      if (uploadingImages.length === 0) {
        setTotalProgress(100);
        return;
      }

      const total = previewImages.reduce(
        (acc, img) => acc + (img.uploadProgress || 0),
        0
      );
      const average = total / previewImages.length;

      setTotalProgress(Math.round(average));
    }, [previewImages]);

    const handleFileClick = () => {
      if (fileInputRef.current && !isUploading) {
        fileInputRef.current.click();
      }
    };

    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        const validFiles = [];

        for (const file of files) {
          if (!file.type.startsWith("image/")) {
            showError(`${file.name} is not an image.`);
            continue;
          }

          if (file.size > 10 * 1024 * 1024) {
            showError(`${file.name} exceeds 10MB limit.`);
            continue;
          }

          validFiles.push(file);
        }

        if (validFiles.length > 0) {
          const newImages = validFiles.map((file) => ({
            id: uuidv4(),
            file,
            blobUrl: URL.createObjectURL(file),
            category: "other",
            uploadProgress: 0,
            isUploading: false,
            uploadedUrl: null,
            public_id: null,
          }));

          setPreviewImages((prev) => {
            const updated = [...prev, ...newImages];
            // Set first image as thumbnail if none selected
            if (!thumbnailId && updated.length > 0) {
              setThumbnailId(updated[0].id);
            }
            setTotalProgress(0);
            return updated;
          });
        }
      }

      //  Reset input so same file can be reselected
      e.target.value = "";
    };

    const handleRemoveImage = (id, e) => {
      e.stopPropagation();

      setPreviewImages((prev) => {
        const imageToRemove = prev.find((img) => img.id === id);
        if (imageToRemove?.blobUrl) {
          URL.revokeObjectURL(imageToRemove.blobUrl);
        }

        const updated = prev.filter((img) => img.id !== id);

        // If removed image was thumbnail, set new thumbnail
        if (thumbnailId === id && updated.length > 0) {
          setThumbnailId(updated[0].id);
        } else if (updated.length === 0) {
          setThumbnailId(null);
          setUploadComplete(false);
        }

        //  Notify parent instantly
        if (onChange) {
          const uploadedImages = updated
            .filter((img) => img.uploadedUrl)
            .map((img) => ({
              url: img.uploadedUrl,
              public_id: img.public_id,
              category: img.category,
              isThumbnail: img.id === thumbnailId,
            }));

          onChange(uploadedImages);
        }

        return updated;
      });
    };

    const handleCategoryChange = (id, category) => {
      setPreviewImages((prev) =>
        prev.map((img) => (img.id === id ? { ...img, category } : img))
      );
    };

    const handleThumbnailSelect = (id, e) => {
      e.stopPropagation();
      if (!isUploading) {
        setThumbnailId(id);
      }
    };

    const handleFullScreen = (image, e) => {
      e.stopPropagation();
      setFullScreenImage(image);
    };

    const handleContinue = async () => {
      if (isUploading) return;
      setIsUploading(true);
      const controllers = [];
      try {
        const uploadTasks = previewImages
          .filter((image) => !image.uploadedUrl)
          .map(async (image) => {
            setPreviewImages((prev) =>
              prev.map((img) =>
                img.id === image.id
                  ? { ...img, isUploading: true, uploadProgress: 0 }
                  : img
              )
            );
            const { uploadPromise, cancel } = uploadFileToCloudinary(
              image.file,
              (progress) => {
                const updated = previewRef.current.map((img) =>
                  img.id === image.id
                    ? { ...img, uploadProgress: progress }
                    : img
                );
                setPreviewImages(updated);
              }
            );
            controllers.push({ id: image.id, cancel });

            try {
              const result = await uploadPromise;
              const updated = previewRef.current.map((img) =>
                img.id === image.id
                  ? {
                      ...img,
                      isUploading: false,
                      uploadedUrl: result.url,
                      public_id: result.public_id,
                    }
                  : img
              );
              setPreviewImages(updated);
              return { id: image.id, success: true };
            } catch (error) {
              console.error("Upload failed:", error);
              const updated = previewRef.current.map((img) =>
                img.id === image.id
                  ? { ...img, isUploading: false, uploadProgress: 0 }
                  : img
              );
              setPreviewImages(updated);
              return { id: image.id, success: false, error };
            }
          });
        setUploadControllers(controllers);

        await Promise.all(uploadTasks);

        // Check if all images are uploaded after completion
        setPreviewImages((prev) => {
          const allUploaded = prev.every((img) => img.uploadedUrl);
          setUploadComplete(allUploaded);
          return prev;
        });

        setIsUploading(false);
      } catch (error) {
        console.error("Upload process error:", error);
        setIsUploading(false);
        setUploadControllers([]);
      }
    };

    const handleCancelUpload = () => {
      // Cancel all ongoing uploads
      uploadControllers.forEach(({ cancel }) => cancel());
      setUploadControllers([]);
      setIsUploading(false);

      // Reset uploading states
      const updatd = previewRef.current.map((img) => ({
        ...img,
        isUploading: false,
        uploadProgress: 0,
      }));
      setPreviewImages(updatd);

      setTotalProgress(0);
      // Check if all images are uploaded
      const allUploaded = previewRef.current.every((img) => img.uploadedUrl);
      setUploadComplete(allUploaded);
    };

    //CONTROLL THE CONTINUE FROM THE PARENT
    useImperativeHandle(ref, () => ({
      triggerUpload: async () => {
        if (uploadComplete) {
          return previewRef.current
            .filter((img) => img.uploadedUrl)
            .map((img) => ({
              url: img.uploadedUrl,
              public_id: img.public_id,
              category: img.category,
              isThumbnail: img.id === thumbnailId,
            }));
        }

        await handleContinue();

        return previewRef.current
          .filter((img) => img.uploadedUrl)
          .map((img) => ({
            url: img.uploadedUrl,
            public_id: img.public_id,
            category: img.category,
            isThumbnail: img.id === thumbnailId,
          }));
      },
    }));

    return (
      <div className="relative">
        <div
          onClick={() => {
            if (!isUploading && previewImages?.length === 0) {
              handleFileClick();
            }
          }}
          className="border-2 border-dashed border-gray-300 max-h-[60vh] overflow-y-auto rounded-lg px-3 py-2 text-center hover:border-primary transition-colors cursor-pointer"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />

          {previewImages.length === 0 && (
            <div className="py-4 sm:py-8">
              <div className="flex justify-center mb-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center relative">
                  <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  {badge && (
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {badge}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileClick();
                }}
                className="text-primary text-sm sm:text-base font-medium hover:underline"
              >
                {buttonText}
              </button>

              <p className="text-xs text-text-muted mt-2">{description}</p>
            </div>
          )}

          {previewImages.length > 0 && (
            <div className="grid grid-cols-2  gap-3 py-3">
              {previewImages.map((image) => (
                <div
                  key={image.id}
                  className="relative group overflow-hidden rounded-lg border border-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={image.uploadedUrl || image.blobUrl}
                      alt="preview"
                      className="object-cover w-full h-full"
                    />

                    {/* Loading Overlay */}
                    {image.isUploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center">
                          <AiOutlineLoading3Quarters className="w-8 h-8 text-white animate-spin mx-auto mb-2" />
                          <span className="text-white text-xs font-medium">
                            {image.uploadProgress}%
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Upload Success Checkmark */}
                    {image.uploadedUrl && !image.isUploading && (
                      <div className="absolute top-1 left-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}

                    {/* Top Right - Delete */}
                    <button
                      onClick={(e) => handleRemoveImage(image.id, e)}
                      disabled={isUploading}
                      className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>

                    {/* Bottom Left - Make Thumbnail */}
                    <button
                      onClick={(e) => handleThumbnailSelect(image.id, e)}
                      disabled={isUploading}
                      className={`absolute bottom-1 left-1 px-2 py-1 text-xs rounded transition-opacity ${
                        thumbnailId === image.id
                          ? "bg-primary text-white"
                          : "bg-black/60 text-white opacity-0 group-hover:opacity-100"
                      } disabled:cursor-not-allowed`}
                    >
                      {thumbnailId === image.id ? "Cover" : "Make Cover"}
                    </button>

                    {/* Bottom Right - Magnify */}
                    <button
                      onClick={(e) => handleFullScreen(image, e)}
                      className="absolute bottom-1 right-1 p-1.5 bg-black/60 hover:bg-black/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaSearchPlus className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>

                  {/* Category Dropdown */}
                  <div className="p-2 bg-white">
                    <select
                      value={image.category}
                      onChange={(e) =>
                        handleCategoryChange(image.id, e.target.value)
                      }
                      disabled={isUploading}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {categoryOptions.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}

              {/* Add More Images Button */}
              <div
                ref={addMoreBtnRef}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileClick();
                }}
                className="relative overflow-hidden rounded-lg border-2 border-dashed border-gray-300 hover:border-primary transition-colors cursor-pointer bg-gray-50 hover:bg-blue-50"
              >
                <div className="aspect-square flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                    <FaImage className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-primary text-xs font-medium">
                    Add More
                  </span>
                </div>

                {/* Matching bottom section height */}
                <div className="p-2 bg-white border-t border-gray-200">
                  <div className="h-6 flex items-center justify-center">
                    <span className="text-xs text-gray-400">
                      Click to upload
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Continue/Uploaded Button */}
        {previewImages.length > 0 && (
          <UploadProgressButton
            key={"progressbtn"}
            uploadComplete={uploadComplete}
            handleCancelUpload={handleCancelUpload}
            handleContinue={handleContinue}
            isUploading={isUploading}
            uploadProgress={totalProgress}
          />
        )}
        {/* Full Screen Modal */}
        {fullScreenImage &&
          createPortal(
            <div
              className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
              onClick={() => setFullScreenImage(null)}
            >
              <button
                onClick={() => setFullScreenImage(null)}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10"
              >
                <FaTimes className="w-6 h-6 text-white" />
              </button>

              <img
                src={fullScreenImage.uploadedUrl || fullScreenImage.blobUrl}
                alt="Full screen preview"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
                {fullScreenImage.category}
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }
);

export default ImageUploadSection;

// useEffect(() => {
//   if (prefillData && prefillData.length > 0) {
//     setPreviewImages(prefillData);
//   }
// }, [prefillData]);
